// apps/console/src/hooks/useRobotAlerts.ts
//
// React view over the robot-alerts-store. Mirrors the open-alert list into
// component state and exposes ack/resolve mutations that call robotsApi and
// then drop the alert optimistically (kept on failure so the operator can
// retry). The store itself is fed by useLiveChannel — this hook is read +
// mutate only.

import { useCallback, useEffect, useState } from 'react'

import type { AlertSeverity } from '../components/organisms/AlertInbox/alert-severity'
import { robotsApi } from '../services/api/domains/robots.api'
import { removeRobotAlert, subscribeRobotAlerts, type RobotAlertItem } from '../services/api/robot-alerts-store'
import { useRobotAlertsPolling } from './useRobotAlertsPolling'

/** Scope for a "Clear all" — mirrors the inbox's robot scope + severity filter. */
export interface ClearAllFilter {
  robotId?: string
  /**
   * The severities to clear, in the BACKEND's vocabulary.
   *
   * This goes over the wire to a Mongo `$in`, so the old console-only `warning` matched **zero**
   * documents — the UI showed rows it believed it was clearing and the server cleared nothing.
   * Callers must pass normalised values (`normalizeSeverity`).
   */
  severity?: AlertSeverity[]
}

export interface UseRobotAlertsResult {
  alerts: RobotAlertItem[]
  count: number
  ack: (alertId: string) => Promise<void>
  resolve: (alertId: string) => Promise<void>
  clearAll: (filter: ClearAllFilter, visibleIds: string[]) => Promise<void>
}

export function useRobotAlerts(): UseRobotAlertsResult {
  const [alerts, setAlerts] = useState<RobotAlertItem[]>([])

  // The live channel is the fast path; this is the one that notices when the fast path stopped.
  // Refcounted, so mounting the bell, the inbox and the dashboard element still yields one poll.
  useRobotAlertsPolling()

  // subscribeRobotAlerts pushes the current list synchronously on subscribe,
  // so initial state is correct without a separate read.
  useEffect(() => subscribeRobotAlerts(setAlerts), [])

  const ack = useCallback(async (alertId: string) => {
    await robotsApi.acknowledgeAlert(alertId)
    removeRobotAlert(alertId)
  }, [])

  const resolve = useCallback(async (alertId: string) => {
    await robotsApi.resolveAlert(alertId)
    removeRobotAlert(alertId)
  }, [])

  // "Clear all": resolve the ENTIRE scope server-side (not just the loaded ids —
  // so the backlog beyond the ≤50 snapshot cap is cleared too and doesn't
  // re-seed on refresh), then optimistically drop the currently-visible rows.
  // Kept on failure so the operator can retry — same posture as ack/resolve.
  const clearAll = useCallback(async (filter: ClearAllFilter, visibleIds: string[]) => {
    await robotsApi.resolveAllAlerts(filter)
    visibleIds.forEach(removeRobotAlert)
  }, [])

  return { alerts, count: alerts.length, ack, resolve, clearAll }
}
